import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createContractApi,
  createBillApi,
  updateContractApi,
  updateBillApi,
  patchContractApi,
  patchBillApi,
  deleteContractApi,
  deleteBillApi,
  deleteContractsApi,
  deleteBillsApi,
  uploadContractFile,
  uploadBillFile,
} from '../api/contractApi'
import { type Contract, type Bill } from '../utils/types'
import LogTool from '../../../logger/logTools'
import { useSmartPatchMutation } from '../../../api/hooks/useSmartMutations'
import { contractKeys, billKeys } from '../utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'

const log = new LogTool({ context: 'ContractMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new contract
 */
export function useCreateContract(): UseMutationResult<Contract, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createContractApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      log.info('Contract created successfully')
    },
    onError: (error) => {
      log.error('Error creating contract:', error)
    },
  })
}

/**
 * Hook to create a new bill
 */
export function useCreateBill(): UseMutationResult<Bill, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createBillApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billKeys.lists() })
      log.info('Bill created successfully')
    },
    onError: (error) => {
      log.error('Error creating bill:', error)
    },
  })
}

/**
 * Hook to update a contract (full update with PUT)
 */
export function useUpdateContract(): UseMutationResult<
  Contract,
  Error,
  { idOrSelf: string | number; data: any; expand?: string[] }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateContractApi(idOrSelf, data),
    onSuccess: async (updatedContract) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: contractKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: Contract) =>
              item.key === updatedContract.key ? updatedContract : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data
      await queryClient.invalidateQueries({ 
        queryKey: contractKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: contractKeys.details(Number(getPKfromSelf(updatedContract.self)))
      })
      log.info('Contract updated successfully')
    },
    onError: (error) => {
      log.error('Error updating contract:', error)
    },
  })
}

/**
 * Hook to update a bill (full update with PUT)
 */
export function useUpdateBill(): UseMutationResult<
  Bill,
  Error,
  { idOrSelf: string | number; data: any; expand?: string[] }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateBillApi(idOrSelf, data),
    onSuccess: async (updatedBill) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: billKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: Bill) =>
              item.key === updatedBill.key ? updatedBill : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data
      await queryClient.invalidateQueries({ 
        queryKey: billKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: billKeys.details(Number(getPKfromSelf(updatedBill.self)))
      })
      log.info('Bill updated successfully')
    },
    onError: (error) => {
      log.error('Error updating bill:', error)
    },
  })
}

/**
 * Hook to patch a contract (partial update with PATCH)
 */
export function usePatchContract(): UseMutationResult<
  Contract,
  Error,
  { idOrSelf: string | number; data: Partial<any>; expand?: string[] }
> {
  const queryClient = useQueryClient()

  return useSmartPatchMutation({
    queryKeyFactory: contractKeys,
    mutationFn: ({ idOrSelf, data }) => patchContractApi(idOrSelf, data),
    onSuccess: (updated) => {
      log.info('Success patching contract:', updated.key)
    },
    onError: (error) => {
      log.error('Error patching contract:', error)
    },
  }, queryClient)
}

/**
 * Hook to patch a bill (partial update with PATCH)
 */
export function usePatchBill(): UseMutationResult<
  Bill,
  Error,
  { idOrSelf: string | number; data: Partial<any>; expand?: string[] }
> {
  const queryClient = useQueryClient()

  return useSmartPatchMutation({
    queryKeyFactory: billKeys,
    mutationFn: ({ idOrSelf, data }) => patchBillApi(idOrSelf, data),
    onSuccess: (updated) => {
      log.info('Success patching bill:', updated.key)
    },
    onError: (error) => {
      log.error('Error patching bill:', error)
    },
  }, queryClient)
}

/**
 * Hook to delete a single contract
 */
export function useDeleteContract(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteContractApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      log.info('Contract deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting contract:', error)
    },
  })
}

/**
 * Hook to delete a single bill
 */
export function useDeleteBill(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteBillApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billKeys.lists() })
      log.info('Bill deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting bill:', error)
    },
  })
}

/**
 * Hook to delete multiple contracts
 */
export function useDeleteContracts(): UseMutationResult<void, Error, Contract[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteContractsApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      log.info('Contracts deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting contracts:', error)
    },
  })
}

/**
 * Hook to delete multiple bills
 */
export function useDeleteBills(): UseMutationResult<void, Error, Bill[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteBillsApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billKeys.lists() })
      log.info('Bills deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting bills:', error)
    },
  })
}

/**
 * Hook to upload file to contract
 */
export function useUploadContractFile(): UseMutationResult<
  void,
  Error,
  { contractSelf: string; file: File }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ contractSelf, file }) => uploadContractFile(contractSelf, file),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: contractKeys.details(Number(getPKfromSelf(variables.contractSelf))),
      })
      log.info('File uploaded to contract successfully')
    },
    onError: (error) => {
      log.error('Error uploading file to contract:', error)
    },
  })
}

/**
 * Hook to upload file to bill
 */
export function useUploadBillFile(): UseMutationResult<
  void,
  Error,
  { billSelf: string; file: File }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ billSelf, file }) => uploadBillFile(billSelf, file),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: billKeys.details(Number(getPKfromSelf(variables.billSelf))),
      })
      log.info('File uploaded to bill successfully')
    },
    onError: (error) => {
      log.error('Error uploading file to bill:', error)
    },
  })
}
